<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="680"
    persistent
    scrollable
  >
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header pa-4">
        <v-icon color="white" class="mr-2">mdi-email-edit-outline</v-icon>
        <span>Mensagem Pos-Consulta</span>
        <v-spacer></v-spacer>
        <v-btn icon dark class="close-btn" :disabled="loading || sending" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <!-- Carregando -->
        <div
          v-if="loading"
          class="d-flex flex-column align-center justify-center py-8"
        >
          <v-progress-circular indeterminate color="primary" size="48" width="4" />
          <span class="text-body-1 mt-3 primary--text font-weight-medium">
            Gerando a mensagem para o paciente...
          </span>
          <span class="text-caption grey--text mt-1">Pode levar alguns segundos.</span>
        </div>

        <!-- Erro -->
        <v-alert v-else-if="errorMessage" type="error" dense outlined class="mb-0">
          <div class="text-body-2">{{ errorMessage }}</div>
        </v-alert>

        <!-- Resultado editavel -->
        <div v-else-if="generated">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-message-text</v-icon>
                <span>Mensagem</span>
              </div>
            </div>
            <v-text-field
              v-if="channel === 'email'"
              v-model="subject"
              label="Assunto do e-mail"
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-format-title"
              class="mb-3"
            />

            <v-textarea
              v-model="message"
              label="Mensagem (edite antes de enviar)"
              outlined
              dense
              auto-grow
              rows="10"
              :hint="patientNameUsed ? `Saudacao: ${patientNameUsed}` : ''"
              persistent-hint
            />
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-send</v-icon>
                <span>Enviar por</span>
              </div>
            </div>
            <v-btn-toggle v-model="channel" mandatory dense color="primary">
              <v-btn value="email" small>
                <v-icon left small>mdi-email-outline</v-icon>
                E-mail
              </v-btn>
              <v-btn value="whatsapp" small>
                <v-icon left small>mdi-whatsapp</v-icon>
                WhatsApp
              </v-btn>
            </v-btn-toggle>
          </section>
        </div>

        <!-- Vazio -->
        <v-alert v-else type="info" dense outlined class="mb-0">
          Sem dados suficientes para gerar a mensagem. Grave a consulta ou adicione
          notas/resumo antes.
        </v-alert>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn
          v-if="generated && !loading"
          text
          color="primary"
          :disabled="sending"
          @click="copyAll"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Copiar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="errorMessage && !loading"
          color="primary"
          outlined
          @click="generate"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Tentar novamente
        </v-btn>
        <v-btn outlined color="primary" :disabled="loading || sending" @click="close">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>
        <v-btn
          v-if="generated && !loading"
          color="primary"
          depressed
          :loading="sending"
          :disabled="!message"
          @click="send"
        >
          <v-icon left small>mdi-send</v-icon>
          Enviar {{ channel === "whatsapp" ? "WhatsApp" : "E-mail" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  gerarMensagemPosConsulta,
  enviarMensagemPorEmail,
  enviarMensagemPorWhatsapp,
} from "@/services/mensagemPosConsultaService";

export default {
  name: "PostConsultationEmailDialog",
  props: {
    value: { type: Boolean, default: false },
    payload: { type: Object, default: () => null },
    patientId: { type: [Number, String], default: null },
  },
  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },
  data() {
    return {
      loading: false,
      sending: false,
      generated: false,
      errorMessage: "",
      subject: "",
      message: "",
      patientNameUsed: "",
      channel: "email",
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        this.generate();
      }
    },
  },
  methods: {
    reset() {
      this.generated = false;
      this.errorMessage = "";
      this.subject = "";
      this.message = "";
      this.patientNameUsed = "";
      this.channel = "email";
    },
    sourceText() {
      const p = this.payload || {};
      if (p.summary && String(p.summary).trim()) return p.summary;
      if (Array.isArray(p.notes) && p.notes.length) return p.notes.join("\n");
      if (p.transcript && String(p.transcript).trim()) return p.transcript;
      return "";
    },
    async generate() {
      const source = this.sourceText();
      if (!source) {
        this.errorMessage =
          "Nenhum conteudo de consulta disponivel para gerar a mensagem.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const res = await gerarMensagemPosConsulta({
          summary: source,
          patientName: this.payload?.patient_name || null,
        });
        if (res.success) {
          this.subject = res.data.subject_email;
          this.message = res.data.email_message;
          this.patientNameUsed = res.data.patient_name_used;
          this.generated = true;
        } else {
          this.errorMessage = res.message || "Falha ao gerar a mensagem.";
        }
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message ||
          err.response?.data?.detail ||
          err.message ||
          "Falha ao gerar a mensagem.";
        this.erroDeRequisicao(err, "Erro ao gerar a mensagem pos-consulta");
      } finally {
        this.loading = false;
      }
    },
    async send() {
      if (!this.message) return;
      if (!this.patientId) {
        this.error("Paciente nao identificado para o envio.");
        return;
      }
      this.sending = true;
      try {
        if (this.channel === "whatsapp") {
          const res = await enviarMensagemPorWhatsapp({
            patientId: this.patientId,
            message: this.message,
          });
          if (res.success === false) throw new Error(res.message || "Falha no envio.");
          this.success("Mensagem enviada por WhatsApp.");
        } else {
          const res = await enviarMensagemPorEmail({
            patientId: this.patientId,
            subject: this.subject,
            message: this.message,
          });
          if (!res.success) throw new Error(res.message || "Falha no envio.");
          this.success("Mensagem enviada por e-mail.");
        }
        this.close();
      } catch (err) {
        this.erroDeRequisicao(err,
          // `error` vem antes de `message`: e o campo com o texto CURADO do
          // backend (ex.: "a janela de 24h do WhatsApp esta fechada"), escrito
          // pra ser lido pelo medico. Sem isso sobra o texto generico do axios
          // ("Request failed with status code 409"), que nao ajuda ninguem.
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Nao foi possivel enviar a mensagem."
        );
      } finally {
        this.sending = false;
      }
    },
    close() {
      this.$emit("input", false);
    },
    async copyAll() {
      const combined =
        this.channel === "email"
          ? `${this.subject}\n\n${this.message}`
          : this.message;
      try {
        await navigator.clipboard.writeText(combined);
        this.success("Mensagem copiada");
      } catch {
        this.error("Nao foi possivel copiar para o clipboard");
      }
    },
  },
};
</script>
